import { Building2, Flame } from 'lucide-react';
import { PortalStatusDetail, PositionStage } from '@clawhire/shared';
import { cn } from '@/lib/utils';
import { EMPTY, formatDate, formatRate } from '@/lib/format';
import { EnumBadge } from '@/components/data/EnumBadge';
import { TONE_CLASSES } from '@/components/data/tones';
import type { PositionBase, PositionComputed, PositionRates } from '../types';

type Size = 'sm' | 'md';

export function StageBadge({ stage, size = 'md' }: { stage: PositionBase['stage']; size?: Size }) {
  return <EnumBadge def={PositionStage} value={stage} size={size} dot />;
}

/** Portal status detail badge with the date of the last check next to it. */
export function PortalBadge({
  position,
  size = 'md',
  showChecked = true,
}: {
  position: Pick<PositionBase, 'portalStatusDetail' | 'portalCheckedAt'>;
  size?: Size;
  showChecked?: boolean;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <EnumBadge def={PortalStatusDetail} value={position.portalStatusDetail} size={size} />
      {showChecked ? (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {position.portalCheckedAt ? (
            <>
              checked <time dateTime={position.portalCheckedAt}>{formatDate(position.portalCheckedAt)}</time>
            </>
          ) : (
            'never checked'
          )}
        </span>
      ) : null}
    </span>
  );
}

function FlagBadge({
  tone,
  icon: Icon,
  children,
  size,
  title,
}: {
  tone: 'danger' | 'warning';
  icon: typeof Flame;
  children: React.ReactNode;
  size: Size;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-md border font-medium',
        size === 'sm' ? 'px-1.5 py-0 text-[11px] leading-5' : 'px-2 py-0.5 text-xs leading-5',
        TONE_CLASSES[tone],
      )}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {children}
    </span>
  );
}

/** Urgent / on-site flags computed by the API. Renders nothing when neither applies. */
export function FlagBadges({ computed, size = 'md' }: { computed: PositionComputed; size?: Size }) {
  if (!computed.isUrgent && !computed.onSiteBlocked) return null;
  return (
    <>
      {computed.isUrgent ? (
        <FlagBadge tone="danger" icon={Flame} size={size} title="Start is close and nobody is past Screening">
          Urgent
        </FlagBadge>
      ) : null}
      {computed.onSiteBlocked ? (
        <FlagBadge
          tone="warning"
          icon={Building2}
          size={size}
          title="Regular on-site presence – not taken on"
        >
          On-site
        </FlagBadge>
      ) : null}
    </>
  );
}

/** "60 EUR/h → ceiling 48" (client rate with the supplier ceiling), or an en dash. */
export function RateLine({
  position,
  rates,
  className,
}: {
  position: Pick<PositionBase, 'clientRateEur' | 'clientRateUnit'>;
  rates: Pick<PositionRates, 'supplierCeilingDisplay'>;
  className?: string;
}) {
  if (position.clientRateEur === null || position.clientRateEur === undefined) {
    return <span className={cn('text-muted-foreground', className)}>{EMPTY}</span>;
  }
  return (
    <span className={cn('whitespace-nowrap tabular-nums', className)}>
      {formatRate(position.clientRateEur, position.clientRateUnit)}
      {rates.supplierCeilingDisplay !== null && rates.supplierCeilingDisplay !== undefined ? (
        <span className="text-muted-foreground"> → ceiling {rates.supplierCeilingDisplay}</span>
      ) : null}
    </span>
  );
}
